import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useLayer } from './layer-stack';
import { NavIcon } from './nav-icon';
import type { NavIconName } from './nav-icon';
import { canAccess, navItemFor } from './nav-items';
import type { NavItem, SectionId } from './nav-items';
import { StatusChip } from './status-chip';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import type { AdminSearchResults } from '../mock/seeds/schema-types';
import { phonePlan } from '../theme/phone-plan';
import { formatPhone } from '@bookingdei/i18n/phone';

// CB-17 ⌘K global search palette (design D:262-301, script D:8495-8543).
// Searches what the contract supports: `GET /admin/search?q` (API-206) returns
// businesses (listings), bookings and customers (users), already filtered by the
// caller's section grants server-side; the "Go to" group matches the sections the
// viewer can open. Category pills narrow to one group (3 rows per group in All,
// 14 in a single group — design `gsPer`); ArrowUp/Down move, Enter opens, Escape
// closes (layer stack). The query is debounced so typing is one request, not one
// per key. Rows land on the owning section; opening the exact record there is the
// section screen's job (DC94/DC99/DC100).

export const PALETTE_DEBOUNCE_MS = 180;
const PER_GROUP_ALL = 3;
const PER_GROUP_SINGLE = 14;

interface Row {
  id: string;
  title: string;
  sub?: string;
  meta?: string;
  status?: { entity: 'booking_status'; value: string };
  to: string;
}

interface Group {
  id: 'nav' | SectionId;
  label: string;
  icon: NavIconName;
  tone: 'accent' | 'neutral' | 'warn';
  rows: Row[];
}

export function CommandPalette({
  open,
  onClose,
  items,
  grants,
}: {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  grants: readonly string[];
}) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<Group['id'] | 'all'>('all');
  const [sel, setSel] = useState(0);
  const [results, setResults] = useState<AdminSearchResults | null>(null);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useLayer(open, onClose);

  // Fresh palette on every open (design gsReset).
  useEffect(() => {
    if (!open) return;
    setQ('');
    setCat('all');
    setSel(0);
    setResults(null);
    setFailed(false);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const term = q.trim();
  useEffect(() => {
    if (!open || !term) {
      setResults(null);
      setFailed(false);
      return;
    }
    const controller = new AbortController();
    const h = setTimeout(async () => {
      try {
        const res = (await api.GET('/admin/search', {
          signal: controller.signal,
          params: { query: { q: term.slice(0, 100) } },
        })) as AdminSearchResults;
        setResults(res);
        setFailed(false);
      } catch {
        if (!controller.signal.aborted) {
          setResults(null);
          setFailed(true);
        }
      }
    }, PALETTE_DEBOUNCE_MS);
    return () => {
      clearTimeout(h);
      controller.abort();
    };
  }, [open, term]);

  const groups = useMemo<Group[]>(() => {
    const out: Group[] = [];
    const lower = term.toLowerCase();
    const nav = items.filter((i) => !lower || t(i.key).toLowerCase().includes(lower));
    out.push({
      id: 'nav',
      label: t('admin.shell.palette.go_to'),
      icon: 'layers',
      tone: 'accent',
      rows: nav.map((i) => ({ id: `nav:${i.section}`, title: t(i.key), to: i.to })),
    });
    if (results) {
      const bookings = navItemFor('bookings');
      if (canAccess(grants, 'bookings') && results.bookings?.length)
        out.push({
          id: 'bookings',
          label: t(bookings.key),
          icon: bookings.icon,
          tone: 'neutral',
          rows: results.bookings.map((b) => ({
            id: `bk:${b.id}`,
            title: b.customer_name ?? b.code,
            sub: [b.code, b.business?.name].filter(Boolean).join(' · '),
            status: { entity: 'booking_status', value: b.status },
            to: bookings.to,
          })),
        });
      const merchants = navItemFor('businesses');
      if (canAccess(grants, 'businesses') && results.businesses?.length)
        out.push({
          id: 'businesses',
          label: t(merchants.key),
          icon: merchants.icon,
          tone: 'accent',
          rows: results.businesses.map((b) => ({
            id: `biz:${b.id}`,
            title: lang === 'bn' && b.name_bn ? b.name_bn : b.name,
            sub: b.area_name ?? b.slug,
            to: merchants.to,
          })),
        });
      const customers = navItemFor('customers');
      if (canAccess(grants, 'customers') && results.users?.length)
        out.push({
          id: 'customers',
          label: t(customers.key),
          icon: customers.icon,
          tone: 'accent',
          rows: results.users.map((u) => ({ id: `usr:${u.id}`, title: u.name, sub: u.phone ? formatPhone(u.phone, phonePlan) : undefined, to: customers.to })),
        });
    }
    return out.filter((g) => g.rows.length > 0);
  }, [items, results, term, grants, lang, t]);

  const single = cat !== 'all' && groups.some((g) => g.id === cat);
  const shown = single ? groups.filter((g) => g.id === cat) : groups;
  const per = single ? PER_GROUP_SINGLE : PER_GROUP_ALL;
  const flat = shown.flatMap((g) => g.rows.slice(0, per));
  const selIdx = Math.max(0, Math.min(sel, flat.length - 1));

  const go = (row: Row | undefined) => {
    if (!row) return;
    onClose();
    navigate(row.to);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel(Math.min(selIdx + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel(Math.max(selIdx - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[selIdx]);
    }
  };

  if (!open || typeof document === 'undefined') return null;
  const num = (n: number) => localiseDigits(lang, n);
  let idx = 0;
  const activeId = flat[selIdx] ? `pal-row-${flat[selIdx].id}` : undefined;

  return createPortal(
    <div className="palette-layer">
      <div className="palette__backdrop" onClick={onClose} aria-hidden="true" data-backdrop />
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('admin.shell.palette.title')}>
        <div className="palette__top">
          <span className="palette__icon">
            <NavIcon name="search" size={17} />
          </span>
          <input
            ref={inputRef}
            className="palette__input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={onKey}
            placeholder={t('admin.shell.search_placeholder')}
            aria-label={t('admin.shell.palette.title')}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            aria-activedescendant={activeId}
            maxLength={100}
          />
          <button type="button" className="palette__esc" onClick={onClose} aria-label={t('admin.shell.palette.close')}>
            {t('admin.shell.palette.esc')}
          </button>
        </div>
        <div className="palette__cats" role="group" aria-label={t('admin.shell.palette.title')}>
          <button
            type="button"
            className={!single ? 'palette__cat is-active' : 'palette__cat'}
            aria-pressed={!single}
            onClick={() => {
              setCat('all');
              setSel(0);
            }}
          >
            {t('admin.shell.palette.all')}
          </button>
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              className={single && cat === g.id ? 'palette__cat is-active' : 'palette__cat'}
              aria-pressed={single && cat === g.id}
              onClick={() => {
                setCat(g.id);
                setSel(0);
              }}
            >
              {formatMessage(lang, 'admin.shell.palette.category', { label: g.label, n: num(g.rows.length) })}
            </button>
          ))}
        </div>
        <div className="palette__body" id="palette-results" role="listbox" aria-label={t('admin.shell.palette.title')}>
          {failed && <div className="palette__empty" role="alert">{t('admin.shell.palette.error')}</div>}
          {!failed && flat.length === 0 && <div className="palette__empty">{t('admin.shell.palette.empty')}</div>}
          {shown.map((g) => (
            <div key={g.id} role="presentation">
              <div className="palette__group" role="presentation">
                <span>{g.label}</span>
                <span className="palette__count">{num(g.rows.length)}</span>
              </div>
              {g.rows.slice(0, per).map((r) => {
                const i = idx++;
                return (
                  <div
                    key={r.id}
                    id={`pal-row-${r.id}`}
                    role="option"
                    aria-selected={i === selIdx}
                    className={i === selIdx ? 'palette__row is-active' : 'palette__row'}
                    onClick={() => go(r)}
                    onMouseEnter={() => setSel(i)}
                  >
                    <span className={`palette__row-icon palette__row-icon--${g.tone}`}>
                      <NavIcon name={g.id === 'nav' ? (items.find((it) => it.to === r.to)?.icon ?? g.icon) : g.icon} size={16} />
                    </span>
                    <span className="palette__row-text">
                      <span className="palette__row-title">{r.title}</span>
                      {r.sub && <span className="palette__row-sub">{r.sub}</span>}
                    </span>
                    {r.status && <StatusChip entity={r.status.entity} status={r.status.value} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="palette__foot" aria-hidden="true">
          <span>
            <kbd className="palette__kbd">↵</kbd>
            {t('admin.shell.palette.open')}
          </span>
          <span>
            <kbd className="palette__kbd">{t('admin.shell.palette.esc')}</kbd>
            {t('admin.shell.palette.close')}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
